import { parse, TextNode, HTMLElement } from 'node-html-parser';
import { makeid } from '@speechki-frontend/shared/helpers/functions';

export const SSML_TAGS_SETTINGS = {
    'say-as': {
        edgeSymbols: 'outside',
        applies: 'single',
        seflClosing: false
    },
    sub: {
        edgeSymbols: 'outside',
        applies: 'multiple',
        seflClosing: false
    },
    prosody: {
        edgeSymbols: 'inside',
        applies: 'mutliple',
        seflClosing: false
    },
    break: {
        edgeSymbols: 'inside',
        applies: 'mutliple',
        seflClosing: true
    },
    phoneme: {
        edgeSymbols: 'outside',
        applies: 'single',
        seflClosing: false
    },
    emphasis: {
        edgeSymbols: 'inside',
        applies: 'mutliple',
        seflClosing: false
    }
};

const BEFORE_REGEX = /(^[([«"'])(.*)/;
const AFTER_REGEX = /(.*)([.,!?:;»"')\]]$)/;
const PUNCTUATION_REGEX = /^[.,!?:;()«"»[\]–-]+$/g;
const INWORD_PUNCTUATION_REGEX = /[.,!-?:;()«"»[\]]/;
const NUMBER_REGEX = /^[0-9.:,()—]+$/;

export default class SSMLParser {
    constructor(options = {}) {
        // SpeakerSettings
        this.options = options;
    }

    // parsing
    parse(ssml) {
        if (!ssml.startsWith('<speak>') || !ssml.endsWith('</speak>')) {
            ssml = `<speak>${ssml}</speak>`;
        }

        const root = parse(ssml).childNodes[0];

        return this.parseNode(root);
    }

    parseNode(node, parent = null) {
        let _parent = parent;

        if (node instanceof HTMLElement) {
            const { tagName, attributes, childNodes } = node;

            const tag = {
                id: makeid(),
                type: 'tag',
                name: tagName,
                attributes: attributes,
                children: [],
                parent: null
            };

            if (_parent) {
                tag.parent = _parent;
                tag.level = _parent.level + 1;
                _parent.children.push(tag);
            } else {
                tag.level = 1;
                _parent = tag;
            }

            childNodes.forEach(child => {
                this.parseNode(child, tag);
            });
        } else if (node instanceof TextNode) {
            const words = node.text.split(/\s/).reduce((acc, cur, index, array) => {
                let item = {
                    id: makeid(),
                    parent: _parent,
                    level: _parent.level ? _parent.level + 1 : 0
                };

                if (cur === '' && index === 0) {
                    // push break slot,
                    item = {
                        ...item,
                        type: 'space',
                        name: 'space',
                        attributes: {
                            time: null
                        }
                    };
                    acc.push(item);
                    return acc;
                } else if (cur === '' && index === array.length - 1 && array.join('') !== '') {
                    // skip last empty element
                    return acc;
                } else if (cur.match(PUNCTUATION_REGEX)) {
                    // parse word
                    // check for punct
                    item = {
                        ...item,
                        type: 'punctuation',
                        name: 'word',
                        before: '',
                        after: '',
                        text: cur,
                        source: cur
                    };
                    acc.push(item);
                } else if (cur.match(NUMBER_REGEX)) {
                    // check for number
                    item = {
                        ...item,
                        type: 'number',
                        name: 'word',
                        before: '',
                        after: '',
                        text: cur,
                        source: cur
                    };
                    acc.push(item);
                } else if (cur.match(INWORD_PUNCTUATION_REGEX)) {
                    const beforeMatch = BEFORE_REGEX.exec(cur);
                    const afterMatch = AFTER_REGEX.exec(cur);

                    let source = beforeMatch ? cur.slice(beforeMatch[1].length) : cur;
                    source = afterMatch ? source.slice(0, -afterMatch[2].length) : source;

                    item = {
                        ...item,
                        type: 'word',
                        name: 'word',
                        before: beforeMatch ? beforeMatch[1] : '',
                        after: afterMatch ? afterMatch[2] : '',
                        text: this.replaceStressSymbol(cur),
                        source,
                        attributes: {}
                    };
                    acc.push(item);
                } else if (cur !== '') {
                    item = {
                        ...item,
                        type: 'word',
                        name: 'word',
                        before: '',
                        after: '',
                        text: this.replaceStressSymbol(cur),
                        source: cur,
                        attributes: {}
                    };
                    acc.push(item);
                }

                if (cur !== '' && index !== array.length - 1) {
                    const space = {
                        id: makeid(),
                        type: 'space',
                        name: 'space',
                        attributes: {
                            time: null
                        },
                        parent: _parent
                    };
                    acc.push(space);
                }

                return acc;
            }, []);

            if (_parent) {
                _parent.children = [..._parent.children, ...words];
            } else {
                throw new Error('Unexpected parentless text node');
            }
        }

        // clear children from false spaces
        _parent.children = this.clearFalseSpaces(_parent.children);

        return _parent;
    }

    get stressSymbol() {
        if (
            !this.options.accent ||
            this.options.accent_type !== 'symbol' ||
            !this.options.accent_symbol
        ) {
            return '';
        }

        return this.options.accent_symbol.replace(/\+/g, '\\$&');
    }

    get stressRegexp() {
        if (!this.stressSymbol) return null;

        return new RegExp(this.stressSymbol, 'g');
    }

    replaceStressSymbol(text) {
        if (this.stressRegexp) {
            return text.replace(this.stressRegexp, '');
        }

        return text;
    }

    clearFalseSpaces(children) {
        return children.slice().filter((node, index, array) => {
            const nextNode = array[index + 1];
            const prevNode = array[index - 1];

            if (node.name === 'space') {
                if (
                    (nextNode && nextNode.name === 'break') ||
                    (prevNode && prevNode.name === 'break')
                ) {
                    return false;
                }
            }

            return true;
        });
    }

    // building
    build(node) {
        let ssml = '';

        const { type, name, children, source, attributes, before, after, parent } = node;

        if (type === 'tag') {
            if (name === 'break') {
                ssml = `<break${makeAttributes(attributes)} />`;
            } else {
                let inner = '';

                const isPhoneme = name === 'phoneme';

                if (children) {
                    children.forEach(child => {
                        inner += this.build(child);
                    });
                }

                ssml = `${isPhoneme ? node.children[0].before : ''}<${node.name}${makeAttributes(
                    node.attributes
                )}>${inner}</${node.name}>${isPhoneme ? node.children[0].after : ''}`;
            }
        } else if (name === 'space') {
            return ' ';
        } else {
            const isParentPhoneme = parent.name === 'phoneme';

            return `${isParentPhoneme ? '' : before}${source}${isParentPhoneme ? '' : after}`;
        }

        if (ssml.startsWith('<speak>') && ssml.endsWith('</speak>')) {
            return ssml.slice(7, -8); // Remove <speak> and </speak>
        }

        return ssml;
    }
}

function makeAttributes(attributes = {}) {
    const entries = Object.entries(attributes);

    if (!entries.length) return '';

    return ` ${entries
        .map(attr => {
            if (attr[1]) {
                return `${attr[0]}="${attr[1]}"`;
            }

            return '';
        })
        .join(' ')}`;
}

export function isWordType(type) {
    return ['word', 'punctuation', 'number'].includes(type);
}

export function calcTreeHeight(node) {
    let height = 0;

    if (node.children) {
        node.children.forEach(child => {
            height = Math.max(height, calcTreeHeight(child));
        });
    }

    return height + 1;
}

export function fixChildrenLevels(node) {
    if (node.children) {
        node.children.forEach(child => {
            child.level = node.level + 1;
            child.parent = node;
            if (child.children) {
                fixChildrenLevels(child);
            }
        });
    }
}
